import { registerPlugin } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { Preferences } from "@capacitor/preferences";
import { isNative, getPlatform } from "./platform.js";
import { api } from "../api/client.js";

// Plugin propio (android/.../PushAvailabilityPlugin.java): responde si este APK
// trae configuración de Firebase.
const PushAvailability = registerPlugin("PushAvailability");

// Marca de "este usuario apagó las notificaciones en ESTE teléfono".
//
// El registro corre en cada arranque (App.jsx). Sin esta marca, apagar el
// interruptor duraba hasta la siguiente apertura de la app: el token se volvía
// a registrar solo y el switch aparecía encendido sin que nadie lo tocara.
const OPTOUT_KEY = "push.mobile.optout";

// Último token conocido de este equipo, persistido.
//
// `currentToken` (abajo) solo se llena cuando el evento "registration" llega en
// ESTA sesión de la app. Apagar el interruptor antes de eso —o después de un
// arranque con opt-out, donde el registro ni corre— dejaba al backend con el
// token vivo: el interruptor decía "no activadas" y el teléfono seguía sonando.
const TOKEN_KEY = "push.mobile.token";

// Id de la fila de `device_tokens` que representa a ESTE equipo, tal como lo
// devuelve el backend al registrarlo.
//
// Sirve para que la lista de equipos vinculados sepa cuál es ella misma. La
// alternativa —compararse por user agent— falla justo en la flota: dos
// teléfonos del mismo modelo tienen el UA idéntico, así que las dos filas
// decían "(este teléfono)" y desvincular la del otro apagaba las de este.
const ROW_ID_KEY = "push.mobile.rowId";

async function rememberRowId(id) {
  try {
    await Preferences.set({ key: ROW_ID_KEY, value: String(id) });
  } catch {
    /* best-effort */
  }
}

/**
 * Id de este equipo en la lista de vinculados, o null si todavía no se registró
 * en esta instalación (ahí el llamador se las arregla sin saberlo).
 *
 * Se devuelve como STRING, que es como viaja: `device_tokens.id` es `bigserial`
 * (int8) y node-postgres lo entrega como texto para no perder precisión — el
 * backend no registra ningún `setTypeParser`. Convertirlo a número acá hacía
 * que la comparación con el id de la fila (`"42" === 42`) fuera falsa siempre,
 * y con ella se caía en silencio todo lo que cuelga de "es este teléfono".
 */
export async function getDeviceRowId() {
  if (!isNative()) return null;
  try {
    const { value } = await Preferences.get({ key: ROW_ID_KEY });
    return /^\d+$/.test(value ?? "") ? value : null;
  } catch {
    return null;
  }
}

async function readOptOut() {
  try {
    const { value } = await Preferences.get({ key: OPTOUT_KEY });
    return value === "1";
  } catch {
    // Preferences no disponible: se asume que NO hay opt-out. Fallar hacia
    // "manda alertas" es el lado correcto de este error.
    return false;
  }
}

async function writeOptOut(optedOut) {
  try {
    if (optedOut) await Preferences.set({ key: OPTOUT_KEY, value: "1" });
    else await Preferences.remove({ key: OPTOUT_KEY });
  } catch {
    /* best-effort */
  }
}

async function rememberToken(token) {
  try {
    await Preferences.set({ key: TOKEN_KEY, value: token });
  } catch {
    /* best-effort */
  }
}

async function forgetToken() {
  try {
    await Preferences.remove({ key: TOKEN_KEY });
  } catch {
    /* best-effort */
  }
}

async function recallToken() {
  try {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    return value || null;
  } catch {
    return null;
  }
}

/**
 * ¿Este build puede registrarse en FCM?
 *
 * Hay que preguntarlo ANTES de `PushNotifications.register()`. Sin
 * `google-services.json` —que está gitignoreado, así que cualquier build local
 * lo está— `FirebaseApp` no se inicializa y `register()` lanza una
 * `IllegalStateException` que es **FATAL EXCEPTION en el hilo CapacitorPlugins**:
 * se lleva el proceso entero. No es una promesa rechazada, así que envolver
 * `register()` en try/catch no sirve de nada — para cuando el JS podría
 * reaccionar, la app ya murió.
 *
 * Ante cualquier duda devuelve `false`. Fallar cerrado deja al usuario sin
 * alertas de fondo, que es malo; fallar abierto lo deja sin app, que es peor.
 */
export async function isPushAvailable() {
  if (!isNative()) return false;
  try {
    const { available } = await PushAvailability.check();
    return available === true;
  } catch {
    // Plugin ausente (APK viejo) o error inesperado.
    return false;
  }
}

export async function getPermissionStatus() {
  if (!isNative()) return "unsupported";
  const { receive } = await PushNotifications.checkPermissions();
  return receive;
}

export async function requestPushPermission() {
  if (!isNative()) return "unsupported";
  let { receive } = await PushNotifications.checkPermissions();
  if (receive === "prompt" || receive === "prompt-with-rationale") {
    ({ receive } = await PushNotifications.requestPermissions());
  }
  return receive;
}

// Las pantallas de ajustes del sistema viven en `lib/appSettings.js`: abrir la
// ficha de notificaciones no es un asunto de push más de lo que abrir la ficha
// de la app es un asunto de ubicación. Se re-exportan desde acá para no mover
// los imports de quienes ya las traían de este módulo.
export {
  openNotificationSettings,
  openAlertChannelSettings,
} from "./appSettings.js";

let registered = false;
// Último token recibido del dispositivo (vía el listener "registration").
// Se necesita en logout para des-registrarlo del backend (Task D3).
let currentToken = null;
// Los listeners de Capacitor son globales del plugin: deben adjuntarse UNA sola
// vez. Si registrationError resetea `registered=false`, un reintento posterior
// de registerDeviceToken() NO debe volver a adjuntarlos (se apilarían).
let listenersAttached = false;

// Quién quiere enterarse de que el token de este equipo quedó registrado.
//
// El registro es asíncrono en dos saltos: `register()` resuelve enseguida y el
// token recién llega después, por el evento "registration". La pantalla de
// notificaciones lista los equipos vinculados, así que sin este aviso el
// teléfono que acabás de activar no aparecía en su propia lista hasta el
// siguiente arranque.
const registrationListeners = new Set();

/**
 * @param {() => void} cb
 * @returns {() => void} cleanup
 */
export function onDeviceTokenRegistered(cb) {
  registrationListeners.add(cb);
  return () => registrationListeners.delete(cb);
}

function ensureListeners() {
  if (listenersAttached) return;
  listenersAttached = true;

  PushNotifications.addListener("registration", async ({ value }) => {
    // Guardar el token vigente antes de registrarlo, para poder des-registrarlo
    // en logout aunque la llamada al backend falle (Task D3). Persistido además
    // de en memoria: ver TOKEN_KEY.
    currentToken = value;
    await rememberToken(value);
    try {
      const alta = await api.registerDeviceToken({
        token: value,
        platform: getPlatform(),
        // Identifica el teléfono en la lista de equipos vinculados: el UA del
        // WebView trae marca y modelo ("Android 13; M2101K6G"), mientras que el
        // token de FCM es una cadena opaca que no le dice nada a nadie.
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      });
      // Null cuando el backend respondió 204 (el token es de otra cuenta) o
      // cuando todavía no despliega el `RETURNING id`: la lista se las arregla
      // sin saber cuál fila es esta.
      if (alta?.id != null) await rememberRowId(alta.id);
      for (const cb of registrationListeners) {
        try {
          cb();
        } catch {
          /* un suscriptor roto no puede tumbar a los demás */
        }
      }
    } catch {
      /* reintenta en el próximo arranque */
    }
  });
  PushNotifications.addListener("registrationError", () => {
    registered = false;
  });
}

/**
 * Registra este equipo para recibir push nativo.
 *
 * Corre en cada arranque desde App.jsx y también detrás del interruptor de
 * `/perfil`. Respeta el opt-out: si el usuario apagó las alertas en este
 * teléfono, no vuelve a pedir permiso ni a registrar el token.
 *
 * @param {{ force?: boolean }} [opts] — `force` viene del interruptor: es el
 *   gesto explícito que levanta el opt-out.
 * @returns {Promise<string>} qué pasó: "registered" | "unavailable" |
 *   "opted-out" | el estado del permiso que impidió registrar ("denied", …).
 */
export async function registerDeviceToken({ force = false } = {}) {
  if (!isNative()) return "unsupported";
  if (registered && !force) return "registered";

  // Antes que el permiso: si el build no puede registrarse, pedirle
  // notificaciones al usuario es pedirle algo que no vamos a usar.
  if (!(await isPushAvailable())) return "unavailable";

  if (force) await writeOptOut(false);
  else if (await readOptOut()) return "opted-out";

  const perm = await requestPushPermission();
  if (perm !== "granted") return perm;

  ensureListeners();

  await PushNotifications.register();
  registered = true;
  return "registered";
}

/**
 * Apaga las notificaciones en ESTE teléfono: deja la marca de opt-out y
 * des-registra el token. Es el gesto del interruptor, no el del logout.
 */
export async function disableDevicePush() {
  await writeOptOut(true);
  await unregisterCurrentDevice();
}

/** ¿El usuario apagó las notificaciones en este teléfono? */
export async function isDevicePushOptedOut() {
  if (!isNative()) return false;
  return readOptOut();
}

/**
 * Des-registra el token de este dispositivo del backend (DELETE
 * /api/push/device-token). Se invoca en logout para que un dispositivo que
 * cerró sesión deje de recibir las alertas CRITICAL de ese usuario.
 *
 * No-op en web (!isNative) o si todavía no hay token. Best-effort: un fallo
 * de red no debe romper el logout. Tras des-registrar, resetea el estado para
 * permitir un nuevo registro en el próximo login.
 */
export async function unregisterCurrentDevice() {
  if (!isNative()) return;

  // El de memoria si esta sesión llegó a registrar; si no, el persistido — que
  // es el caso de apagar el interruptor apenas abierta la app.
  const token = currentToken ?? (await recallToken());
  if (!token) return;

  try {
    await api.unregisterDeviceToken(token);
  } catch {
    /* best-effort: el backend igual purga tokens inválidos */
  }
  currentToken = null;
  await forgetToken();
  registered = false;
}
